import React, { useRef, useState, useEffect } from "react";

import { Alert } from "services/Alert";
import styled from "styled-components";
import PageHeader from "components/base/PageHeader";
import PanelLayout from "components/base/PanelLayout";
import ProtectedRangesFilter from "./ProtectedRangesFilter";
import ProtectedRangesTable from "./ProtectedRangesTable";
import {
    setProtectedRangesLoading,
    getProtectedRangesList,
    removeProtectedRangesList,
    uploadProtectedRangesFile
} from "actions/protected_ranges";
import { FilterSectionFlexboxGrid, StyledItemFitWidth, StyledItemAutoMargin } from "components/base/FitContainers";
import { useWindowWidth } from "hooks";
import {
    PROTECTED_RANGES_GET_LIST_API,
    SMS_PROTECTED_RANGES_GET_LIST_API,
    PROTECTED_RANGES_UPLOAD_API,
    SMS_PROTECTED_RANGES_UPLOAD_API,
    PROTECTED_RANGES_CREATE_TEXT_API,
    SMS_PROTECTED_RANGES_CREATE_TEXT_API,
    PROTECTED_RANGES_REMOVE_LIST_API,
    SMS_PROTECTED_RANGES_REMOVE_LIST_API
} from "const/apiMethods";
import { downloadDataAsFile, servicePick } from "utils";
import ProtectedRangesCreateModal from "./ProtectedRangesCreateModal";
import { ButtonPrimary } from "components/base/BaseButton";
import auth from "reducers/auth";
import { checkPermissionsFor } from "store/storeHelpers";


import { connect } from "react-redux";
import { getCsvResponse } from "api/loginRoutes";
import { Button, Tooltip, Uploader } from "rsuite";
import Whisper from "components/Whisper";

const defaultFilter = {};
const defaultSort = { column: "e_code", type: "asc" };

const ProtectedRangesPage = ({
    page,
    perPage,
    count,
    loading,
    service,
    permissions,

    setLoading,
    protectedRangesList,
    getProtectedRangesList,
    removeProtectedRangesList,
    uploadProtectedRangesFile
}) => {

    const uploaderRef = useRef(null);
    const [uploaderKey, setUploaderKey] = useState(0);

    const [showCreateModal, setShowCreateModal] = useState(false);

    const [sort, setSort] = useState(defaultSort);
    const [filter, setFilter] = useState(defaultFilter);
    const [fileList, setFileList] = useState([]);

    const [deleteKeys, setDeleteKeys] = useState([]);

    useEffect(() => {
        // getList
        getProtectedRangesList(filter, service);
    }, [service]);

    const getItems = async (pageValue, perPageValue, sortValue) => {
        // getList with sort
        await getProtectedRangesList(filter, service);
    };


    const onChangeFilters = async (filterValue) => {
        const newFilter = {
            ...(filterValue.str ? { str: filterValue.str } : {})
        };

        setFilter(newFilter);
        // getList
        await getProtectedRangesList(newFilter, service);
    };

    const onDeleteSelectedRanges = () => {
        const removeResponse = removeProtectedRangesList(deleteKeys, service);
        removeResponse.then(async () => {
            await getProtectedRangesList(filter, service);

            const deleteMessage = deleteKeys.length > 1 ? "Prefixes successfully deleted" : "Prefix successfully deleted";
            Alert.success(deleteMessage, 2000);

            setDeleteKeys([]);
        })
    };

    const handleUploadFile = (files) => {
        const params = {};

        if (files && files.length) {
            const dt = new DataTransfer();
            dt.items.add(files[0].blobFile);
            params.files = dt.files;
            setFileList(files);
        }

        const uploadResponse = uploadProtectedRangesFile(params, service);
        uploadResponse.then(async (response) => {
            let createdCount = 0;

            if (response && response["created/updated"]) {
                createdCount = response["created/updated"];
            }

            if (createdCount) {
                Alert.success(`Successfully added ${createdCount} ranges`);
            } else {
                Alert.info("No new ranges were added");
            }

            setFileList([]);
            setUploaderKey(k => k + 1);

            await getProtectedRangesList(filter, service);
        });
    };

    const handleDownloadFile = () => {
        const method = servicePick(service, PROTECTED_RANGES_GET_LIST_API, SMS_PROTECTED_RANGES_GET_LIST_API);

        setLoading(true);
        getCsvResponse(method)
            .then((response) => {
                const file = "protected_ranges.csv";
                downloadDataAsFile(response, file)
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const windowWidth = useWindowWidth();
    const isMobile = windowWidth < 767;


    const uploadMethod = servicePick(service, PROTECTED_RANGES_UPLOAD_API, SMS_PROTECTED_RANGES_UPLOAD_API);
    const downloadMethod = servicePick(service, PROTECTED_RANGES_GET_LIST_API, SMS_PROTECTED_RANGES_GET_LIST_API);
    const createMethod = servicePick(service, PROTECTED_RANGES_CREATE_TEXT_API, SMS_PROTECTED_RANGES_CREATE_TEXT_API);
    const deleteMethod = servicePick(service, PROTECTED_RANGES_REMOVE_LIST_API, SMS_PROTECTED_RANGES_REMOVE_LIST_API);

    const uploadPermission = checkPermissionsFor(uploadMethod, permissions);
    const downloadPermission = checkPermissionsFor(downloadMethod, permissions);
    const createPermission = checkPermissionsFor(createMethod, permissions);
    const deletePermission = checkPermissionsFor(deleteMethod, permissions);

    const disableDeleteButton = !deleteKeys.length || !deletePermission;

    return (
        <PanelLayout>
            <PageHeader title="Protected Ranges" showService={false} />
            <FilterSectionFlexboxGrid>
                <StyledItemFitWidth
                    isMobile={isMobile}
                >
                    <ProtectedRangesFilter
                        filter={filter}
                        defaultFilter={defaultFilter}
                        isMobile={isMobile}

                        onChangeFilter={onChangeFilters}
                    />
                </StyledItemFitWidth>
                <StyledItemAutoMargin
                    isMobile={isMobile}
                >
                    <ButtonGroup>
                        <Whisper
                            key={uploaderKey}
                            speaker={<Tooltip>Use CSV file with columns: prefix;comment</Tooltip>}
                            placement="left"
                        >
                            <WhisperTarget>
                                <StyledUploader
                                    ref={uploaderRef}
                                    disabled={!uploadPermission}
                                    accept="text/csv"
                                    multiple={false}
                                    removable={true}
                                    autoUpload={false}
                                    tooltipText=""
                                    fileList={fileList}
                                    onChange={handleUploadFile}
                                >
                                    <Button>
                                        Upload
                                    </Button>
                                </StyledUploader>
                            </WhisperTarget>
                        </Whisper>
                        <Button
                            disabled={!downloadPermission}
                            onClick={handleDownloadFile}
                        >
                            Download
                        </Button>
                        <Button
                            disabled={!createPermission}
                            onClick={() => {
                                setShowCreateModal(true);
                            }}
                        >
                            Add
                        </Button>
                    </ButtonGroup>
                </StyledItemAutoMargin>
            </FilterSectionFlexboxGrid>
            <ProtectedRangesTable
                windowWidth={windowWidth}
                dataList={protectedRangesList}
                page={page}
                perPage={perPage}
                sort={sort}
                count={count}
                loading={loading}
                isMobile={isMobile}

                deleteKeys={deleteKeys}

                getItems={getItems}
                setDeleteKeys={setDeleteKeys}
            />
            <FilterSectionFlexboxGrid>
                <StyledItemFitWidth></StyledItemFitWidth>
                <StyledItemAutoMargin>
                    <DeleteButton
                        appearance="primary"
                        disabled={disableDeleteButton}
                        onClick={onDeleteSelectedRanges}
                    >
                        Delete selected
                    </DeleteButton>
                </StyledItemAutoMargin>
            </FilterSectionFlexboxGrid>
            <ProtectedRangesCreateModal
                show={showCreateModal}
                loading={loading}
                maxWidth={600}
                windowWidth={windowWidth}
                service={service}
                onClose={() => {
                    setShowCreateModal(false);
                }}
                onSuccess={async () => {
                    setShowCreateModal(false);
                    await getProtectedRangesList(filter, service);
                }}
                setLoading={setLoading}
            />
        </PanelLayout>
    );
};

const mapStateToProps = ({ auth, protected_ranges }) => ({
    perPage: protected_ranges.perPage,
    page: protected_ranges.page,
    count: protected_ranges.count,
    service: auth.service,
    permissions: auth.permissions,
    protectedRangesList: protected_ranges.list,
    operatorList: protected_ranges.operator_list,
    loading: protected_ranges.loading
});

const mapDispatchToProps = {
    setLoading: setProtectedRangesLoading,
    getProtectedRangesList,
    removeProtectedRangesList,
    uploadProtectedRangesFile
};

export default connect(mapStateToProps, mapDispatchToProps)(ProtectedRangesPage);


const ButtonGroup = styled.div`
    && {
        display: flex;
        flex-direction: row;

        align-items: center;
    }

    && button {
        margin-left: 10px; 
        background: #20BA88;
        border: none;
        color: #fff;
        box-shadow: 0 4px 4px rgba(0,0,0,0.16);
        border-radius: 5px;
        padding: 12px 34px;
    }

     && {
        .rs-btn:focus,
        .rs-btn:hover,
        .rs-btn:focus:hover,
        .rs-btn:active:hover,
        .rs-btn:active:focus, 
        .rs-btn:active.focus {
            background-color: var(--color-secondary-hover) !important;
            color: #ffffff !important;
        }

        button.rs-btn.rs-btn-disabled,
        button.rs-btn:disabled {
            background-color: #20BA88 !important;
            box-shadow: none;
            cursor: not-allowed;
            opacity: 0.5;
        }
    }
`;

const WhisperTarget = styled.div`
    display: inline-flex;
`;

const DeleteButton = styled(Button)`
    &&.rs-btn.rs-btn-primary {
        background-color: var(--color-error) !important;
    }
`;

const StyledUploader = styled(Uploader)`
  
    &&.rs-uploader-text .rs-uploader-file-items {
        margin: 0 !important;
        display: none;
    }
`;
